import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import ChatIcon from "@mui/icons-material/ChatBubbleOutline";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PageLayout from "../PageLayout/PageLayout";
import { CURRENCY, MARKET, DIAL_CODE, DELIVERY_LOCATION_EXAMPLE } from "../../config/market";
import useCheckoutDeliverySchedule from "../../hooks/useCheckoutDeliverySchedule";
import DeliveryTimePicker from "../DeliveryTimePicker";

const API_BASE = process.env.REACT_APP_SERVER_URL;
const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_SOCKET_SERVER_URL ||
  process.env.REACT_APP_SERVER_URL;

const CHANNEL_META = {
  IN_PLATFORM: { Icon: ChatIcon,    label: "On Lusuku",  color: "#0a578b", bg: "#eff6ff", border: "#bfdbfe" },
  WHATSAPP:    { Icon: WhatsAppIcon, label: "WhatsApp",   color: "#15803d", bg: "#dcfce7", border: "#86efac" },
  EMAIL:       { Icon: EmailIcon,    label: "Email",      color: "#0a578b", bg: "#eff6ff", border: "#bfdbfe" },
  PHONE:       { Icon: PhoneIcon,    label: "Phone call", color: "#0a578b", bg: "#eff6ff", border: "#bfdbfe" },
};

const STATUS_META = {
  OPEN:      { bg: "#fffbeb", color: "#92400e", dot: "#f59e0b", label: "Open"      },
  CLOSED:    { bg: "#f1f5f9", color: "#475569", dot: "#94a3b8", label: "Closed"    },
  CANCELLED: { bg: "#fff1f2", color: "#be123c", dot: "#fb7185", label: "Cancelled" },
  SENT:      { bg: "#eff6ff", color: "#1d4ed8", dot: "#60a5fa", label: "Quote sent"},
  ACCEPTED:  { bg: "#f0fdf4", color: "#15803d", dot: "#4ade80", label: "Accepted"  },
  EXPIRED:   { bg: "#1e293b", color: "#f8fafc", dot: "#94a3b8", label: "Expired"   },
  // Negotiation states (parity with the mobile apps' quote tracker).
  COUNTER_OFFERED:    { bg: "#eef2ff", color: "#4338ca", dot: "#818cf8", label: "You countered"     },
  REVISION_REQUESTED: { bg: "#fffbeb", color: "#92400e", dot: "#f59e0b", label: "Changes requested" },
  REJECTED:           { bg: "#fff1f2", color: "#be123c", dot: "#fb7185", label: "Declined"          },
  SUPERSEDED:         { bg: "#f1f5f9", color: "#64748b", dot: "#cbd5e1", label: "Replaced"          },
  AWAITING_VENDOR:     { bg: "#fffbeb", color: "#92400e", dot: "#f59e0b", label: "Awaiting vendor"   },
  QUOTED:              { bg: "#eff6ff", color: "#1d4ed8", dot: "#60a5fa", label: "Quote received"    },
  NEGOTIATING:         { bg: "#eef2ff", color: "#4338ca", dot: "#818cf8", label: "Negotiating"       },
  FULFILLED:           { bg: "#f0fdf4", color: "#15803d", dot: "#4ade80", label: "Completed"         },
  DECLINED:            { bg: "#fff1f2", color: "#be123c", dot: "#fb7185", label: "Declined"          },
};

// Statuses a buyer can still act on (accept / counter / request change / decline).
const NEGOTIABLE = ["SENT", "COUNTER_OFFERED", "REVISION_REQUESTED"];
const ACTIVE_REQUEST_LIFECYCLES = [
  "AWAITING_VENDOR",
  "QUOTED",
  "NEGOTIATING",
];

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - Date.now()) / 86400000);
  return diff;
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || { bg: "#f1f5f9", color: "#475569", dot: "#94a3b8", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 700, letterSpacing: 0.3,
      background: m.bg, color: m.color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
};

const ChannelChip = ({ channel }) => {
  const m = CHANNEL_META[channel] || CHANNEL_META.IN_PLATFORM;
  const Icon = m.Icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 600,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
    }}>
      <Icon style={{ fontSize: 13 }} />
      {m.label}
    </span>
  );
};

const MyQuotes = () => {
  const history = useHistory();
  const user = useSelector((state) => state.user.currentUser);
  const authToken = user?.token || user?.accessToken || user?.result?.token;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const [requests, setRequests] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(null);
  const [liveConnected, setLiveConnected] = useState(false);
  const socketRef = useRef(null);

  const [acceptModal, setAcceptModal] = useState(null);
  const [phone, setPhone] = useState("");
  const [addressText, setAddressText] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("COD");
  // Accepting a quote CREATES an order, so it carries a delivery time like any
  // other checkout: the same picker, the same 45 minute freshness check and the
  // same dialog when the server refuses the window. `brandId` may arrive
  // populated, so read the id off either shape.
  const quoteBrandId = acceptModal?.brandId?._id || acceptModal?.brandId || null;
  const deliveryTime = useCheckoutDeliverySchedule({
    flow: "brand",
    vendorId: quoteBrandId,
  });
  // Delivery is Lusuku's own line on top of the vendor's quote. Price it as
  // the buyer types the address so the total they confirm is the total they
  // pay (server: POST /api/brands/quotes/:id/delivery-preview).
  const [deliveryPreview, setDeliveryPreview] = useState({ status: "idle", data: null });
  useEffect(() => {
    if (!acceptModal?._id || addressText.trim().length < 4) {
      setDeliveryPreview({ status: "idle", data: null });
      return undefined;
    }
    let cancelled = false;
    setDeliveryPreview((prev) => ({ ...prev, status: "loading" }));
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API_BASE}/api/brands/quotes/${acceptModal._id}/delivery-preview`,
          { shippingAddress: { address: { address: addressText.trim() } } },
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
        );
        if (!cancelled) setDeliveryPreview({ status: "ready", data: res.data?.data || null });
      } catch (_) {
        if (!cancelled) setDeliveryPreview({ status: "error", data: null });
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [acceptModal?._id, addressText, authToken]);

  // ── Negotiation (parity with the mobile apps) ───────────────────────────
  const [counterModal, setCounterModal] = useState(null); // quote being countered
  const [counterAmount, setCounterAmount] = useState("");
  const [counterNote, setCounterNote] = useState("");
  const [revisionModal, setRevisionModal] = useState(null); // quote needing changes
  const [revisionNote, setRevisionNote] = useState("");
  const [negotiating, setNegotiating] = useState(false);
  const [requestBusy, setRequestBusy] = useState(null);

  const fetchData = useCallback(async () => {
    if (!authToken) return;
    const hdrs = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    setLoading(true);
    try {
      const [reqRes, qRes] = await Promise.all([
        axios.get(`${API_BASE}/api/brands/my/quote-requests`, { headers: hdrs }),
        axios.get(`${API_BASE}/api/brands/my/quotes`, { headers: hdrs }),
      ]);
      setRequests(reqRes.data.data || []);
      setQuotes(qRes.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load quotes.");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Real-time socket: subscribe to buyer quote events ──────────────────
  const buyerId =
    user?._id || user?.id || user?.result?._id || user?.result?.id || null;

  useEffect(() => {
    if (!buyerId || !SOCKET_URL) return undefined;

    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("join", `buyerQuoteRoom:${buyerId}`);
      setLiveConnected(true);
    });
    socket.on("disconnect", () => setLiveConnected(false));

    socket.on("brandQuote:requestCreated", (payload) => {
      // The buyer might have created the request from this same tab; in that
      // case the optimistic update will collide with the next fetchData.
      // We dedupe on _id below.
      setRequests((prev) => {
        if (prev.some((r) => String(r._id) === String(payload?.requestId))) return prev;
        const synthetic = {
          _id: payload.requestId,
          title: payload.title,
          status: payload.status || "OPEN",
          lifecycleStatus: payload.lifecycleStatus || "AWAITING_VENDOR",
          preferredChannel: payload.preferredChannel,
          responseDueAt: payload.responseDueAt,
          productSnapshot: payload.product,
          createdAt: payload.createdAt || new Date().toISOString(),
        };
        return [synthetic, ...prev];
      });
    });

    socket.on("brandQuote:quoteSent", (payload) => {
      toast.info("\u2728 A vendor just sent you a quote \u2014 check below.");
      // Re-fetch to get the full quote document with line items.
      fetchData();
    });

    socket.on("brandQuote:statusUpdated", (payload) => {
      setRequests((prev) =>
        prev.map((r) =>
          String(r._id) === String(payload?.requestId)
            ? {
                ...r,
                status: payload.status || r.status,
                lifecycleStatus:
                  payload.lifecycleStatus || r.lifecycleStatus,
              }
            : r
        )
      );
    });
    socket.on("brandQuote:requestUpdated", (payload) => {
      setRequests((prev) =>
        prev.map((request) =>
          String(request._id) === String(payload?.requestId)
            ? { ...request, ...payload }
            : request
        )
      );
      fetchData();
    });
    socket.on("brandOrder:stageChanged", () => fetchData());

    return () => {
      socket.off("brandQuote:requestCreated");
      socket.off("brandQuote:quoteSent");
      socket.off("brandQuote:statusUpdated");
      socket.off("brandQuote:requestUpdated");
      socket.off("brandOrder:stageChanged");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [buyerId, fetchData]);

  const openAcceptModal = (quote) => {
    setAcceptModal(quote);
    setPhone("");
    setAddressText("");
    setPaymentMethod("COD");
  };

  const submitAccept = async () => {
    if (!acceptModal) return;
    setAccepting(acceptModal._id);
    // One guarded path: the picker stops the submit and opens its dialog when
    // the window is missing or stale, and reads DELIVERY_SLOT_INVALID /
    // SCHEDULED_ORDERS_ONLY off the refusal.
    const outcome = await deliveryTime.submitRequest(async (deliverySchedule) => {
      const res = await axios.post(
        `${API_BASE}/api/brands/quotes/${acceptModal._id}/accept`,
        {
          shippingAddress: {
            customerNumber: phone,
            address: { address: addressText },
          },
          paymentMethod,
          deliverySchedule,
        },
        { headers }
      );
      return res.data;
    });
    setAccepting(null);
    if (!outcome.ok) {
      // A stop or a delivery-time refusal is already on screen in the picker's
      // dialog; anything else is the server's own reason.
      if (!outcome.stopped && !outcome.handled) {
        toast.error(
          outcome.error?.response?.data?.message || "Could not accept quote."
        );
      }
      return;
    }
    toast.success("Quote accepted, order created.");
    setAcceptModal(null);
    fetchData();
    const orderId = outcome.data?.data?.order?._id;
    history.push(orderId ? `/orderDetail/${orderId}` : "/my_profile/my_orders");
  };

  // ── Buyer negotiation actions ─────────────────────────────────────────
  const submitCounter = async () => {
    if (!counterModal) return;
    const amount = Number(counterAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return toast.error("Enter the amount you want to propose.");
    }
    setNegotiating(true);
    try {
      await axios.post(
        `${API_BASE}/api/brands/quotes/${counterModal._id}/counter-offer`,
        { amount, message: counterNote },
        { headers }
      );
      toast.success("Counter-offer sent to the vendor.");
      setCounterModal(null);
      setCounterAmount("");
      setCounterNote("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not send counter-offer.");
    } finally {
      setNegotiating(false);
    }
  };

  const submitRevision = async () => {
    if (!revisionModal) return;
    if (!revisionNote.trim()) {
      return toast.error("Tell the vendor what you'd like changed.");
    }
    setNegotiating(true);
    try {
      await axios.post(
        `${API_BASE}/api/brands/quotes/${revisionModal._id}/request-revision`,
        { note: revisionNote },
        { headers }
      );
      toast.success("Change request sent to the vendor.");
      setRevisionModal(null);
      setRevisionNote("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not send the request.");
    } finally {
      setNegotiating(false);
    }
  };

  const declineQuote = async (quote) => {
    if (!window.confirm("Decline this quote? The vendor will be notified.")) return;
    setNegotiating(true);
    try {
      await axios.post(
        `${API_BASE}/api/brands/quotes/${quote._id}/reject`,
        {},
        { headers }
      );
      toast.success("Quote declined.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not decline the quote.");
    } finally {
      setNegotiating(false);
    }
  };

  const cancelRequest = async (request) => {
    if (!window.confirm("Cancel this quote request? The vendor will be notified.")) return;
    setRequestBusy(request._id);
    try {
      await axios.post(
        `${API_BASE}/api/brands/quote-requests/${request._id}/cancel`,
        {},
        { headers }
      );
      toast.success("Quote request cancelled.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not cancel the request.");
    } finally {
      setRequestBusy(null);
    }
  };

  const resolveExternalRequest = async (request) => {
    if (!window.confirm("Mark this off-platform quote request as completed?")) return;
    setRequestBusy(request._id);
    try {
      await axios.post(
        `${API_BASE}/api/brands/quote-requests/${request._id}/resolve`,
        {},
        { headers }
      );
      toast.success("Quote request marked as completed.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not close the request.");
    } finally {
      setRequestBusy(null);
    }
  };

  if (!authToken) {
    return (
      <PageLayout>
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <ReceiptLongIcon style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 12 }} />
          <div style={{ color: "#64748b", fontWeight: 600, fontSize: "1rem" }}>Sign in to view your quotes.</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "0 4px 40px" }}>

        {/* ── Page header ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: "1.25rem" }}>My Quotes</h4>
            <span
              title={liveConnected ? "Receiving real-time updates" : "Reconnecting…"}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: "0.68rem", fontWeight: 700, letterSpacing: 0.3,
                color: liveConnected ? "#15803d" : "#94a3b8",
                background: liveConnected ? "#dcfce7" : "#f1f5f9",
                padding: "3px 9px", borderRadius: 20,
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: liveConnected ? "#16a34a" : "#cbd5e1",
                display: "inline-block",
                boxShadow: liveConnected ? "0 0 0 2px #bbf7d0" : "none",
              }} />
              {liveConnected ? "Live" : "Offline"}
            </span>
          </div>
          <button
            onClick={fetchData}
            title="Refresh"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: "none", border: "1px solid #e2e8f0",
              borderRadius: 8, padding: "5px 12px",
              cursor: "pointer", fontSize: "0.8rem", color: "#475569",
              fontWeight: 600,
            }}
          >
            <RefreshIcon style={{ fontSize: 16 }} />
            Refresh
          </button>
        </div>

        {/* ── Section: Quote Requests ── */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1, color: "#94a3b8", textTransform: "uppercase", marginBottom: 10 }}>
            Quote Requests
          </div>

          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[1, 2].map((i) => (
                <div key={i} style={{ height: 88, borderRadius: 12, background: "#f1f5f9", animation: "pulse 1.5s infinite" }} />
              ))}
            </div>
          ) : requests.length === 0 ? (
            <div style={{
              textAlign: "center", padding: "40px 24px",
              borderRadius: 14, background: "#f8fafc",
              border: "1.5px dashed #e2e8f0",
            }}>
              <ReceiptLongIcon style={{ fontSize: 40, color: "#cbd5e1", marginBottom: 10 }} />
              <div style={{ color: "#475569", fontWeight: 700, fontSize: "0.95rem", marginBottom: 4 }}>No quote requests yet</div>
              <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>
                Visit a brand shop and tap <strong>"Price on request"</strong> to get started.
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {requests.map((r) => {
                const requestStatus =
                  r.lifecycleStatus ||
                  (r.status === "CLOSED" ? "QUOTED" : r.status);
                const sm = STATUS_META[requestStatus] || STATUS_META.OPEN;
                const days = daysUntil(r.desiredDeadline);
                const urgent = days !== null && days >= 0 && days <= 3;
                const overdue = days !== null && days < 0;
                const requestIsActive =
                  ACTIVE_REQUEST_LIFECYCLES.includes(requestStatus);
                const responseDueAt = r.responseDueAt
                  ? new Date(r.responseDueAt)
                  : null;
                const responseOverdue =
                  responseDueAt && responseDueAt.getTime() < Date.now();
                const externalChannel = r.preferredChannel !== "IN_PLATFORM";
                const brandName =
                  r.brandId?.tradingName || r.brandId?.name || "";
                return (
                  <div key={r._id} style={{
                    display: "flex", alignItems: "stretch",
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                  }}>
                    {/* Left accent */}
                    <div style={{ width: 4, flexShrink: 0, background: sm.dot }} />

                    {/* Card body */}
                    <div style={{ flex: 1, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Row 1: title + status */}
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem", lineHeight: 1.3 }}>{r.title}</div>
                          {brandName && (
                            <div style={{ fontSize: "0.73rem", color: "#64748b", marginTop: 2 }}>
                              {brandName}
                            </div>
                          )}
                          {r.productSnapshot?.name && (
                            <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 2 }}>
                              {r.productSnapshot.name}
                              {r.productSnapshot.skuCode && (
                                <span style={{
                                  marginLeft: 6, padding: "1px 6px", borderRadius: 4,
                                  background: "#f1f5f9", color: "#64748b", fontSize: "0.68rem", fontWeight: 600,
                                }}>
                                  {r.productSnapshot.skuCode}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <StatusPill status={requestStatus} />
                      </div>

                      {/* Row 2: chips + dates */}
                      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <ChannelChip channel={r.preferredChannel} />

                        {r.desiredDeadline && (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            fontSize: "0.72rem", fontWeight: 600,
                            color: overdue ? "#be123c" : urgent ? "#b45309" : "#64748b",
                            background: overdue ? "#fff1f2" : urgent ? "#fffbeb" : "#f8fafc",
                            padding: "3px 9px", borderRadius: 20,
                            border: `1px solid ${overdue ? "#fecdd3" : urgent ? "#fde68a" : "#e2e8f0"}`,
                          }}>
                            {overdue
                              ? `⚠ Overdue`
                              : days === 0
                              ? "Due today"
                              : `Due in ${days}d`}
                            {" · "}{new Date(r.desiredDeadline).toLocaleDateString(MARKET.currencyLocale, { day: "numeric", month: "short" })}
                          </span>
                        )}

                        {requestStatus === "AWAITING_VENDOR" && responseDueAt && (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            fontSize: "0.72rem", fontWeight: 600,
                            color: responseOverdue ? "#be123c" : "#475569",
                            background: responseOverdue ? "#fff1f2" : "#f8fafc",
                            padding: "3px 9px", borderRadius: 20,
                            border: `1px solid ${responseOverdue ? "#fecdd3" : "#e2e8f0"}`,
                          }}>
                            {responseOverdue ? "Vendor reply overdue" : "Vendor reply expected"}
                            {" · "}{responseDueAt.toLocaleString(MARKET.currencyLocale, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                          </span>
                        )}

                        <span style={{ fontSize: "0.7rem", color: "#94a3b8", marginLeft: "auto" }}>
                          Updated {new Date(r.lastActivityAt || r.createdAt).toLocaleDateString(MARKET.currencyLocale, { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      </div>

                      {r.resolution?.note && (
                        <div style={{ fontSize: "0.75rem", color: "#64748b", background: "#f8fafc", borderRadius: 7, padding: "6px 9px" }}>
                          Closure note: {r.resolution.note}
                        </div>
                      )}

                      {requestIsActive && (
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                          {externalChannel && (
                            <button
                              type="button"
                              disabled={requestBusy === r._id}
                              onClick={() => resolveExternalRequest(r)}
                              style={{ border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#15803d", borderRadius: 7, padding: "6px 10px", fontSize: "0.74rem", fontWeight: 700, cursor: "pointer" }}
                            >
                              Mark resolved
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={requestBusy === r._id}
                            onClick={() => cancelRequest(r)}
                            style={{ border: "1px solid #fecdd3", background: "#fff", color: "#be123c", borderRadius: 7, padding: "6px 10px", fontSize: "0.74rem", fontWeight: 700, cursor: "pointer" }}
                          >
                            Cancel request
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Section: Quotes Received ── */}
        {quotes.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1, color: "#94a3b8", textTransform: "uppercase", marginBottom: 10 }}>
              Quotes Received
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {quotes.map((q) => {
                const validDays = daysUntil(q.validUntil);
                const expired = validDays !== null && validDays < 0;
                return (
                  <div key={q._id} style={{
                    borderRadius: 14, border: "1px solid #e2e8f0",
                    background: "#fff", overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}>
                    {/* Quote top band */}
                    <div style={{
                      background: q.status === "ACCEPTED" ? "#f0fdf4" : q.status === "SENT" ? "#eff6ff" : "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                      padding: "14px 18px",
                      display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
                    }}>
                      <div>
                        <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>
                          Quote total
                        </div>
                        <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                          {q.currency || CURRENCY} {Number(q.totalAmount).toLocaleString(MARKET.currencyLocale)}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                        <StatusPill status={q.status} />
                        {q.validUntil && (
                          <span style={{ fontSize: "0.72rem", color: expired ? "#be123c" : "#64748b" }}>
                            {expired ? "⚠ Expired" : `Valid until ${new Date(q.validUntil).toLocaleDateString(MARKET.currencyLocale, { day: "numeric", month: "short", year: "numeric" })}`}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quote body */}
                    <div style={{ padding: "12px 18px" }}>
                      {/* Line items */}
                      {q.lineItems?.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          {q.lineItems.map((li, i) => (
                            <div key={i} style={{
                              display: "flex", justifyContent: "space-between", alignItems: "center",
                              padding: "6px 0",
                              borderBottom: i < q.lineItems.length - 1 ? "1px solid #f1f5f9" : "none",
                              fontSize: "0.82rem",
                            }}>
                              <span style={{ color: "#334155", flex: 1 }}>{li.description}</span>
                              <span style={{ color: "#64748b", marginLeft: 12, whiteSpace: "nowrap" }}>
                                {li.quantity} × {Number(li.unitPrice).toLocaleString(MARKET.currencyLocale)}
                              </span>
                              <span style={{ fontWeight: 700, color: "#0f172a", marginLeft: 16, whiteSpace: "nowrap" }}>
                                {Number(li.quantity * li.unitPrice).toLocaleString(MARKET.currencyLocale)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {q.notes && (
                        <div style={{
                          fontSize: "0.8rem", color: "#64748b", fontStyle: "italic",
                          background: "#f8fafc", borderRadius: 8, padding: "8px 12px",
                          marginBottom: 12,
                        }}>
                          "{q.notes}"
                        </div>
                      )}

                      {/* Waiting-on-vendor banners for negotiation rounds */}
                      {q.status === "COUNTER_OFFERED" && (
                        <div style={{
                          fontSize: "0.8rem", color: "#4338ca", background: "#eef2ff",
                          border: "1px solid #c7d2fe", borderRadius: 8,
                          padding: "8px 12px", marginBottom: 12,
                        }}>
                          You proposed <strong>{q.counterOffer?.currency || q.currency || CURRENCY}{" "}
                          {Number(q.counterOffer?.amount || 0).toLocaleString(MARKET.currencyLocale)}</strong>.
                          The vendor will reply with a revised quote — or you can still accept the
                          original terms above.
                        </div>
                      )}
                      {q.status === "REVISION_REQUESTED" && (
                        <div style={{
                          fontSize: "0.8rem", color: "#92400e", background: "#fffbeb",
                          border: "1px solid #fde68a", borderRadius: 8,
                          padding: "8px 12px", marginBottom: 12,
                        }}>
                          You asked for changes{q.revisionNote ? `: "${q.revisionNote}"` : ""}.
                          Waiting for the vendor's revised quote.
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                        {NEGOTIABLE.includes(q.status) && !expired && (
                          <>
                            <button
                              onClick={() => declineQuote(q)}
                              disabled={negotiating}
                              style={{
                                background: "none", color: "#be123c",
                                border: "1px solid #fecdd3", borderRadius: 8,
                                padding: "9px 14px", fontWeight: 700,
                                cursor: "pointer", fontSize: "0.82rem",
                              }}
                            >
                              Decline
                            </button>
                            {q.status !== "REVISION_REQUESTED" && (
                              <button
                                onClick={() => { setRevisionModal(q); setRevisionNote(""); }}
                                disabled={negotiating}
                                style={{
                                  background: "#fff", color: "#475569",
                                  border: "1px solid #e2e8f0", borderRadius: 8,
                                  padding: "9px 14px", fontWeight: 700,
                                  cursor: "pointer", fontSize: "0.82rem",
                                }}
                              >
                                Request change
                              </button>
                            )}
                            {q.status !== "COUNTER_OFFERED" && (
                              <button
                                onClick={() => {
                                  setCounterModal(q);
                                  setCounterAmount("");
                                  setCounterNote("");
                                }}
                                disabled={negotiating}
                                style={{
                                  background: "#fff", color: "#4338ca",
                                  border: "1px solid #c7d2fe", borderRadius: 8,
                                  padding: "9px 14px", fontWeight: 700,
                                  cursor: "pointer", fontSize: "0.82rem",
                                }}
                              >
                                Counter offer
                              </button>
                            )}
                            <button
                              onClick={() => openAcceptModal(q)}
                              disabled={negotiating}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                background: "#017109", color: "#fff",
                                border: "none", borderRadius: 8,
                                padding: "9px 20px", fontWeight: 700,
                                cursor: "pointer", fontSize: "0.85rem",
                                boxShadow: "0 2px 8px rgba(1,113,9,0.25)",
                              }}
                            >
                              <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
                              Accept &amp; place order
                            </button>
                          </>
                        )}
                        {q.status === "ACCEPTED" && q.acceptedOrderId && (
                          <button
                            type="button"
                            onClick={() => history.push(`/orderDetail/${q.acceptedOrderId}`)}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              color: "#15803d", fontWeight: 700, fontSize: "0.85rem",
                              background: "#f0fdf4", border: "1px solid #bbf7d0",
                              borderRadius: 8, padding: "8px 12px", cursor: "pointer",
                            }}
                          >
                            <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
                            {q.acceptedOrder?.brandFlowStage === "DELIVERED"
                              ? "Completed · View order"
                              : q.acceptedOrder?.brandFlowStage === "CANCELLED"
                                ? "Order cancelled · View"
                                : q.acceptedOrder?.brandFlowStage
                                  ? `${q.acceptedOrder.brandFlowStage.replace(/_/g, " ").toLowerCase()} · View order`
                                  : "Order placed · View order"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Accept modal ── */}
      {acceptModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(15,23,42,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16,
          backdropFilter: "blur(2px)",
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440,
            boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
            /* The delivery time picker made this modal taller than a phone
               screen; scroll inside it rather than clipping the buttons. */
            maxHeight: "90vh", overflowY: "auto",
          }}>
            {/* Modal header */}
            <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "#0f172a", marginBottom: 2 }}>Accept this quote</div>
              <div style={{ fontSize: "0.82rem", color: "#64748b" }}>
                Total: <strong style={{ color: "#0f172a" }}>{acceptModal.currency || CURRENCY} {Number(acceptModal.totalAmount).toLocaleString(MARKET.currencyLocale)}</strong>
              </div>
            </div>

            {/* Form */}
            <div style={{ padding: "18px 22px" }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Phone number <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={`${DIAL_CODE} 700 000 000`}
                  style={{
                    width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 8,
                    padding: "9px 12px", fontSize: "0.9rem", boxSizing: "border-box",
                    outline: "none", color: "#0f172a",
                    transition: "border-color 0.15s",
                  }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Delivery address <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressText}
                  onChange={(e) => setAddressText(e.target.value)}
                  placeholder={DELIVERY_LOCATION_EXAMPLE}
                  style={{
                    width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 8,
                    padding: "9px 12px", fontSize: "0.9rem", boxSizing: "border-box",
                    outline: "none", color: "#0f172a",
                  }}
                />
                {deliveryPreview.status === "loading" && (
                  <div style={{ color: "#64748b", fontSize: "0.8rem", marginTop: 6 }}>Pricing delivery to this address</div>
                )}
                {deliveryPreview.status === "ready" && deliveryPreview.data?.priced && (
                  <div style={{ background: "#eef6f5", border: "1px solid #bfe0da", borderRadius: 8, padding: "8px 10px", marginTop: 8, color: "#0f4c45", fontSize: "0.85rem" }}>
                    Delivery: {deliveryPreview.data.currency || CURRENCY} {Number(deliveryPreview.data.deliveryFee).toLocaleString(MARKET.currencyLocale)}
                    {deliveryPreview.data.loadClass && deliveryPreview.data.loadClass !== "MOTORCYCLE" ? ` (by ${deliveryPreview.data.loadClass.toLowerCase()})` : ""}
                    <div style={{ fontWeight: 800, marginTop: 2 }}>
                      Total to pay: {deliveryPreview.data.currency || CURRENCY} {Number(deliveryPreview.data.totalPrice).toLocaleString(MARKET.currencyLocale)}
                    </div>
                  </div>
                )}
                {deliveryPreview.status === "ready" && deliveryPreview.data && !deliveryPreview.data.priced && (
                  <div style={{ background: "#fff8e1", border: "1px solid #f2c94c", borderRadius: 8, padding: "8px 10px", marginTop: 8, color: "#5d4300", fontSize: "0.85rem" }}>
                    {deliveryPreview.data.notice}
                  </div>
                )}
              </div>

              <DeliveryTimePicker controller={deliveryTime} />

              <div style={{ marginBottom: 20, marginTop: 14 }}>
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Payment method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{
                    width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 8,
                    padding: "9px 12px", fontSize: "0.9rem", boxSizing: "border-box",
                    background: "#fff", color: "#0f172a",
                  }}
                >
                  <option value="COD">Cash on delivery</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setAcceptModal(null)}
                  style={{
                    flex: 1, background: "#f1f5f9", border: "none",
                    borderRadius: 8, padding: "10px", cursor: "pointer",
                    fontWeight: 700, color: "#475569", fontSize: "0.88rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitAccept}
                  disabled={!phone.trim() || !addressText.trim() || accepting === acceptModal._id}
                  style={{
                    flex: 2, background: "#017109", color: "#fff",
                    border: "none", borderRadius: 8, padding: "10px",
                    fontWeight: 800, cursor: (!phone.trim() || !addressText.trim()) ? "not-allowed" : "pointer",
                    opacity: (!phone.trim() || !addressText.trim()) ? 0.55 : 1,
                    fontSize: "0.88rem",
                    boxShadow: "0 2px 8px rgba(1,113,9,0.25)",
                  }}
                >
                  {accepting === acceptModal._id ? "Placing order…" : "Confirm & place order"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Counter-offer modal ── */}
      {counterModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(15,23,42,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16, backdropFilter: "blur(2px)",
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440,
            boxShadow: "0 24px 64px rgba(0,0,0,0.22)", overflow: "hidden",
          }}>
            <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "#0f172a", marginBottom: 2 }}>Make a counter-offer</div>
              <div style={{ fontSize: "0.82rem", color: "#64748b" }}>
                Vendor quoted <strong style={{ color: "#0f172a" }}>{counterModal.currency || CURRENCY} {Number(counterModal.totalAmount).toLocaleString(MARKET.currencyLocale)}</strong>
              </div>
            </div>
            <div style={{ padding: "18px 22px" }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Your offer ({counterModal.currency || CURRENCY}) <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={counterAmount}
                  onChange={(e) => setCounterAmount(e.target.value)}
                  placeholder="e.g. 75000"
                  style={{
                    width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 8,
                    padding: "9px 12px", fontSize: "0.9rem", boxSizing: "border-box",
                    outline: "none", color: "#0f172a",
                  }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Note to the vendor (optional)
                </label>
                <textarea
                  rows={3}
                  value={counterNote}
                  onChange={(e) => setCounterNote(e.target.value)}
                  placeholder="e.g. Can you match this for a repeat monthly order?"
                  style={{
                    width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 8,
                    padding: "9px 12px", fontSize: "0.9rem", boxSizing: "border-box",
                    outline: "none", color: "#0f172a", resize: "vertical",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setCounterModal(null)}
                  style={{
                    flex: 1, background: "#f1f5f9", border: "none",
                    borderRadius: 8, padding: "10px", cursor: "pointer",
                    fontWeight: 700, color: "#475569", fontSize: "0.88rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitCounter}
                  disabled={negotiating || !counterAmount}
                  style={{
                    flex: 2, background: "#4338ca", color: "#fff",
                    border: "none", borderRadius: 8, padding: "10px",
                    fontWeight: 800, cursor: !counterAmount ? "not-allowed" : "pointer",
                    opacity: !counterAmount ? 0.55 : 1, fontSize: "0.88rem",
                  }}
                >
                  {negotiating ? "Sending…" : "Send counter-offer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Request-change modal ── */}
      {revisionModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(15,23,42,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16, backdropFilter: "blur(2px)",
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440,
            boxShadow: "0 24px 64px rgba(0,0,0,0.22)", overflow: "hidden",
          }}>
            <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "#0f172a", marginBottom: 2 }}>Request a change</div>
              <div style={{ fontSize: "0.82rem", color: "#64748b" }}>
                Tell the vendor what you'd like different — quantities, specs, delivery, anything.
              </div>
            </div>
            <div style={{ padding: "18px 22px" }}>
              <div style={{ marginBottom: 20 }}>
                <textarea
                  rows={4}
                  value={revisionNote}
                  onChange={(e) => setRevisionNote(e.target.value)}
                  placeholder="e.g. Please quote for 500 units instead of 100, delivered to Ntinda."
                  style={{
                    width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 8,
                    padding: "9px 12px", fontSize: "0.9rem", boxSizing: "border-box",
                    outline: "none", color: "#0f172a", resize: "vertical",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setRevisionModal(null)}
                  style={{
                    flex: 1, background: "#f1f5f9", border: "none",
                    borderRadius: 8, padding: "10px", cursor: "pointer",
                    fontWeight: 700, color: "#475569", fontSize: "0.88rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitRevision}
                  disabled={negotiating || !revisionNote.trim()}
                  style={{
                    flex: 2, background: "#b45309", color: "#fff",
                    border: "none", borderRadius: 8, padding: "10px",
                    fontWeight: 800, cursor: !revisionNote.trim() ? "not-allowed" : "pointer",
                    opacity: !revisionNote.trim() ? 0.55 : 1, fontSize: "0.88rem",
                  }}
                >
                  {negotiating ? "Sending…" : "Send request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default MyQuotes;
